<template>
  <b-modal
    id="register-modal"
    v-model="isVisible"
    size="lg"
    centered
    hide-footer
    hide-header-close
    no-close-on-backdrop
    @hidden="onClose"
  >
    <template #modal-header>
      <div class="d-flex justify-content-between align-items-center w-100">
        <h4 class="modal-title text-white mb-0">
          สมัครสมาชิก
        </h4>
        <b-button variant="link" class="text-white p-0" @click="onClose">
          <feather-icon icon="XIcon" size="24" />
        </b-button>
      </div>
    </template>

    <div class="register-modal-content">
      <form-wizard
        :color="themeColor"
        :title="null"
        :subtitle="null"
        shape="square"
        step-size="sm"
        finish-button-text="สมัครสมาชิก"
        next-button-text="ถัดไป"
        back-button-text="ย้อนกลับ"
        steps-classes="d-flex justify-content-center wizard-nav-custom"
        @on-complete="handleSubmit"
      >
        <!-- Step 1: Phone & Captcha -->
        <tab-content title=">" :before-change="validationForm">
          <validation-observer ref="registerRules" tag="form">
            <b-row>
              <b-col class="mb-1">
                <b-alert variant="warning" show class="mt-1">
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    กรุณาระบุเบอร์โทรศัพท์ที่ใช้ให้ถูกต้องเพื่อรับ SMS
                  </div>
                </b-alert>
                <b-alert
                  ref="alert-message"
                  :show="phoneExist"
                  variant="danger"
                >
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    หมายเลขโทรศัพท์ที่กรอกได้ถูกใช้งานไปแล้ว
                    <router-link
                      :to="{ name: 'login' }"
                      class="text-blue-400 underline"
                    >
                      เข้าสู่ระบบ
                    </router-link>
                  </div>
                </b-alert>
              </b-col>
              <b-col sm="12">
                <input-field
                  id="phone"
                  v-model="form.step1.phone"
                  label="เบอร์โทรศัพท์"
                  :red="true"
                  placeholder="กรุณาระบุเบอร์โทร กรอกเฉพาะตัวเลขเท่านั้นเช่น 0928882345"
                  :rules="'required|numeric|digits:10'"
                  err-message="กรุณาระบุเบอร์โทรศัพท์ให้ถูกต้อง"
                />
              </b-col>
              <b-col sm="12">
                <canvas
                  ref="captchaImage"
                  width="90"
                  height="30"
                  style="
                    border: 1px solid white;
                    background-color: white;
                    border-radius: 4px;
                  "
                />
              </b-col>
              <b-col sm="12">
                <input-field
                  id="captcha"
                  v-model="form.step1.captcha"
                  label="รหัสลับ"
                  :red="true"
                  placeholder="กรุณาระบุรหัสลับตามภาพที่เห็น"
                  :rules="`required|is:${code}`"
                  err-message="กรุณาระบุรหัสลับให้ถูกต้อง"
                />
              </b-col>
            </b-row>
          </validation-observer>
        </tab-content>

        <!-- Step 2: Personal Details & Banking -->
        <tab-content title=">>" :before-change="validationFormInfo">
          <validation-observer ref="infoRules" tag="form">
            <b-row>
              <b-col sm="12" class="mb-2">
                <b-alert variant="warning" show class="mt-1">
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    กรุณาระบุชื่อ-นามสกุลให้ตรงกับบัญชีที่ใช้
                    หากชื่อ-นามกุลไม่ตรงกับบัญชีจะไม่สามารถถอนเงินได้
                  </div>
                </b-alert>
                <b-alert
                  ref="alert-message"
                  :show="accountNoExist"
                  variant="danger"
                >
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    หมายเลขบัญชีที่กรอก ได้ถูกใช้งานไปแล้ว
                    <router-link
                      :to="{ name: 'login' }"
                      class="text-blue-400 underline"
                    >
                      เข้าสู่ระบบ
                    </router-link>
                  </div>
                </b-alert>
              </b-col>
              <b-col sm="6">
                <input-field
                  id="firstname"
                  v-model="form.step2.firstname"
                  label="ชื่อจริง"
                  placeholder="กรุณาระบุชื่อจริง"
                  :rules="'required'"
                  :red="true"
                  err-message="กรุณาระบุชื่อจริง"
                />
              </b-col>
              <b-col sm="6">
                <input-field
                  id="lastname"
                  v-model="form.step2.lastname"
                  label="นามสกุล"
                  placeholder="กรุณาระบุนามสกุล"
                  :rules="'required'"
                  :red="true"
                  err-message="กรุณาระบุนามสกุล"
                />
              </b-col>
              <b-col sm="12">
                <validation-provider
                  #default="{ errors }"
                  name="Banks"
                  rules="required"
                >
                  <b-form-group
                    label="ธนาคาร"
                    label-for="bank"
                    :state="errors.length > 0 ? false : null"
                  >
                    <v-select
                      id="bank"
                      v-model="form.step2.bank"
                      :options="banks"
                      :selectable="
                        option => !option.value.includes('select_value')
                      "
                      label="text"
                    />
                    <b-form-invalid-feedback
                      :state="errors.length > 0 ? false : null"
                    >
                      {{ errors[0] ? 'กรุณาเลือกธนาคาร' : '' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col sm="6">
                <input-field
                  id="bank_account_no"
                  v-model="form.step2.accountNo"
                  label="เลขที่บัญชี"
                  :red="true"
                  placeholder="กรุณาเลขบัญชีเฉพาะตัวเลขเท่านั้น"
                  :rules="'required|numeric|min:10|max:12'"
                  err-message="กรุณาระบุเลขที่บัญชีให้ถูกต้อง"
                />
              </b-col>
            </b-row>
          </validation-observer>
        </tab-content>

        <!-- Step 3: Password & Preferences -->
        <tab-content title="" :before-change="validationFormAddress">
          <validation-observer ref="addressRules" tag="form">
            <b-row>
              <b-col md="6">
                <input-field
                  id="password"
                  v-model="form.step3.password"
                  label="กำหนดรหัสผ่าน"
                  :red="true"
                  placeholder="กรุณากำหนดรหัสผ่าน 6-16 ตัวอักษร"
                  :rules="'required|min:6|max:16'"
                  err-message="กรุณาตั้งรหัสผ่าน 6-16 ตัว"
                />
              </b-col>
              <b-col md="6">
                <input-field
                  id="lineId"
                  v-model="form.step3.lineId"
                  label="LINE ID"
                  :red="true"
                  placeholder="กรุณาระบุ LINE ID"
                  :rules="'required'"
                  err-message="กรุณากรอกไลน์ไอดีเพื่อรับการแจ้งเตือน"
                />
              </b-col>
              <!-- <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="Promotion"
                  rules="required"
                >
                  <b-form-group
                    label="โปรโมชั่น"
                    label-for="promotion"
                    :state="errors.length > 0 ? false : null"
                  >
                    <v-select
                      id="promotion"
                      v-model="form.step3.promotion"
                      :options="promotions"
                      :selectable="
                        option => !option.value.includes('select_value')
                      "
                      label="text"
                    />
                    <b-form-invalid-feedback
                      :state="errors.length > 0 ? false : null"
                    >
                      {{ errors[0] ? 'กรุณาเลือกโปรโมชั่น' : '' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col> -->
              <b-col md="12">
                <b-alert :show="form.step3.promoId !== ''" variant="info">
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    โปรโมชั่นที่รับ: {{ promotionTitle }}
                  </div>
                </b-alert>
                <b-alert :show="form.step3.promoId !== ''" variant="danger">
                  <div class="alert-body">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    เงื่อนไข: โปรโมชั่น รับได้เพียง 1 คนต่อ 1 สิทธิ์
                    หากตรวจสอบพบ สมัครหลายยูสเพื่อรับสิทธิ์ ยูสจะถูกระงับ
                  </div>
                </b-alert>
              </b-col>
            </b-row>
          </validation-observer>
        </tab-content>
      </form-wizard>
    </div>
  </b-modal>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import {
  BModal,
  BButton,
  BFormGroup,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BAlert
} from 'bootstrap-vue'
import getCurrentDomain from '@/utils/domain'
import { required, email } from '@validations'
import InputField from '../../component/InputField.vue'
import jwt from '../../../auth/jwt/useJwt'
import {
  setUserData,
  getPromoId,
  clearPromoId,
  getRefCode,
  clearRefCode,
  getDeviceId
} from '../../../auth/utils'

export default {
  name: 'RegisterModal',
  components: {
    ValidationProvider,
    ValidationObserver,
    FormWizard,
    TabContent,
    BModal,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BAlert,
    vSelect,
    InputField
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    promoId: {
      type: String,
      default: null
    }
  },
  computed: {
    themeColor() {
      return (
        getComputedStyle(document.documentElement)
          .getPropertyValue('--theme-primary')
          .trim() || '#DC2626'
      )
    }
  },
  data() {
    return {
      isVisible: false,
      code: '',
      coupon: '',
      type: '',
      fbc: '',
      fbp: '',
      phoneExist: false,
      isFromAds: false,
      accountNoExist: false,
      promotionTitle: '',
      form: {
        step1: {
          phone: '',
          captcha: ''
        },
        step2: {
          accountNo: '',
          bank: '',
          firstname: '',
          lastname: '',
          firstname_en: '',
          lastname_en: ''
        },
        step3: {
          password: '',
          lineId: '',
          promoCode: '',
          promotion: '',
          cid: '',
          fbp: '',
          ref: '',
          promoId: '',
          device_id: ''
        }
      },
      required,
      email,
      banks: [
        { value: 'select_value', text: 'กรุณาเลือกธนาคาร' },
        { value: '1', text: 'ธนาคารกรุงเทพ' },
        { value: '2', text: 'ธนาคารกรุงศรีอยุธยา' },
        { value: '3', text: 'ธนาคารกรุงไทย' },
        { value: '4', text: 'ธนาคารกสิกรไทย' },
        { value: '5', text: 'ธนาคารไทยพาณิชย์' },
        { value: '22', text: 'ธนาคารไทยธนชาติ' },
        { value: '16', text: 'ธนาคารเกียรตินาคิน' },
        { value: '7', text: 'ธนาคารออมสิน' },
        { value: '10', text: 'ธนาคารยูโอบี' },
        { value: '9', text: 'ธนาคารเพื่อการเกษตร' },
        { value: '12', text: 'ธนาคารอาคารสงเคราะห์' },
        { value: '14', text: 'ธนาคารทิสโก้' },
        { value: '15', text: 'ธนาคารซิตี้แบงก์' },
        { value: '18', text: 'ธนาคารแลนด์ แอนด์ เฮ้าส์' },
        { value: '11', text: 'ธนาคารซีไอเอ็มบี' }
      ],
      promotions: [
        { value: 'select_value', text: 'เลือกโปรโมชั่น' },
        { value: '0', text: 'ไม่รับ' }
      ]
    }
  },
  watch: {
    show: {
      handler(newVal) {
        this.isVisible = newVal
        if (newVal) {
          this.initializeModal()
        }
      },
      immediate: true
    }
  },
  methods: {
    onClose() {
      this.isVisible = false
      this.resetForm()
      this.$emit('close')
    },

    async initializeModal() {
      this.resetForm()
      await this.parseQueryParams()
      this.$nextTick(() => {
        setTimeout(() => {
          this.createCaptcha()
        }, 300)
      })
    },

    parseQueryParams() {
      const urlParams = new URLSearchParams(window.location.search)

      // Parse Facebook Pixel parameters
      const fbp = urlParams.get('fbp')
      const fbc = urlParams.get('fbc')
      const cid = urlParams.get('cid')
      // Affiliate ref: prioritize URL, fallback to localStorage (captured in
      // main.js) so it survives navigations that drop the query string
      const ref = urlParams.get('ref') || getRefCode()

      // Set form data from query params
      if (fbp) this.form.step3.fbp = fbp
      if (cid) this.form.step3.cid = cid
      if (ref) this.form.step3.ref = ref

      // Device fingerprint for invite-friend same-device detection
      this.form.step3.device_id = getDeviceId() || ''

      // Handle Facebook Click ID (fbc)
      if (fbc) {
        this.form.step3.fbp = fbc
      }

      // Get promo_id: prioritize prop (passed from event), fallback to localStorage
      // This ensures promoId is available even if localStorage fails (private mode, quota exceeded, etc.)
      const promoIdValue = this.promoId || getPromoId()
      if (promoIdValue) {
        this.form.step3.promoId = promoIdValue
        this.getPromotionById(promoIdValue)
      }
    },

    createCaptcha() {
      const charsArray = '123456789'
      const lengthOtp = 4
      const captcha = []

      for (let i = 0; i < lengthOtp; i += 1) {
        const index = Math.floor(Math.random() * charsArray.length)
        if (captcha.indexOf(charsArray[index]) === -1) {
          captcha.push(charsArray[index])
        } else {
          i -= 1
        }
      }

      const canvas = this.$refs.captchaImage

      if (!canvas) {
        // Canvas not ready yet, retry after a short delay
        setTimeout(() => {
          this.createCaptcha()
        }, 100)
        return
      }

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.error('Failed to get canvas context')
        return
      }

      // Clear any previous content
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Fill white background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Configure and draw text
      ctx.font = 'bold 22px Arial'
      ctx.fillStyle = '#000000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Draw text in the center
      const text = captcha.join('')
      ctx.fillText(text, canvas.width / 2, canvas.height / 2)

      this.code = text
    },

    async handleSubmit() {
      // Fallback: Re-check promoId before submit if form value is empty
      // This catches edge cases where promoId was lost during form interaction
      if (!this.form.step3.promoId) {
        const fallbackPromoId = this.promoId || getPromoId()
        if (fallbackPromoId) {
          this.form.step3.promoId = fallbackPromoId
        }
      }

      // Same fallback for the affiliate ref
      if (!this.form.step3.ref) {
        const fallbackRef = getRefCode()
        if (fallbackRef) {
          this.form.step3.ref = fallbackRef
        }
      }

      // Show loading state
      this.$swal({
        title: 'กรุณารอสักครู่',
        text: 'ระบบกำลังทำการสมัครสมาชิก..',
        icon: 'info',
        allowOutsideClick: false,
        showCancelButton: false,
        showConfirmButton: false
      })

      try {
        // Submit registration to API
        const resp = await this.$http.post('/internal/register', {
          domain: getCurrentDomain(),
          payload: this.form
        })

        if (resp.status === 201) {
          this.$swal.close()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'สมัครสมาชิกเสร็จเรียบร้อย',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })

          // Set JWT token (extract from nested structure: resp.data.token.token)
          jwt.setToken(resp.data.token.token)

          // Set user data in localStorage
          setUserData(resp.data.member)

          // Clear promo_id and affiliate ref from localStorage after
          // successful registration (avoid mis-attributing a later account)
          clearPromoId()
          clearRefCode()

          // Close modal before redirect
          this.onClose()

          // Redirect to personal info page (use window.location.href like login for full app reinit)
          window.location.href = '/personal-info?from=register'
        } else {
          this.$swal.close()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'พบข้อผิดพลาดกรุณาติดต่อแอดมิน',
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      } catch (error) {
        this.$swal.close()
        console.error('Registration error:', error)
        const errorMessage =
          error.response?.data?.error || 'พบข้อผิดพลาดกรุณาติดต่อแอดมิน'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    resetForm() {
      // Reset all form data
      this.form = {
        step1: {
          phone: '',
          captcha: ''
        },
        step2: {
          accountNo: '',
          bank: '',
          firstname: '',
          lastname: '',
          firstname_en: '',
          lastname_en: ''
        },
        step3: {
          password: '',
          lineId: '',
          promoCode: '',
          promotion: '',
          cid: '',
          fbp: '',
          ref: '',
          promoId: '',
          device_id: getDeviceId() || ''
        }
      }

      // Reset form state flags
      this.phoneExist = false
      this.accountNoExist = false
      this.code = ''

      // Reset validation for all steps
      if (this.$refs.registerRules) {
        this.$refs.registerRules.reset()
      }
      if (this.$refs.infoRules) {
        this.$refs.infoRules.reset()
      }
      if (this.$refs.addressRules) {
        this.$refs.addressRules.reset()
      }
    },

    async checkInfo(fieldName, fieldValue) {
      const resp = await this.$http.post('/internal/check/info', {
        domain: getCurrentDomain(),
        fieldName,
        fieldValue
      })

      return resp.data
    },

    async getPromotionById(promoId) {
      try {
        const response = await this.$http.get(`/internal/promotion/${promoId}`)
        if (response.data && response.data.title) {
          this.promotionTitle = response.data.title
        }
      } catch (error) {
        console.error('Error fetching promotion:', error)
        this.promotionTitle = ''
      }
    },

    async validationForm() {
      const isValid = await this.$refs.registerRules.validate()
      if (isValid) {
        const isExist = await this.checkInfo('phone_no', this.form.step1.phone)
        if (isExist.status === true) {
          this.phoneExist = true
          return false
        }
        return true
      }
      return false
    },

    async validationFormInfo() {
      const isValid = await this.$refs.infoRules.validate()
      if (isValid) {
        const isExist = await this.checkInfo(
          'bank_no',
          this.form.step2.accountNo
        )
        if (isExist.status === true) {
          this.accountNoExist = true
          return false
        }
        return true
      }
      return false
    },

    validationFormAddress() {
      return new Promise((resolve, reject) => {
        this.$refs.addressRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    }
  },
  emits: ['close']
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.vs__dropdown-option {
  white-space: nowrap !important;
}

.register-modal-content {
  color: white;
  font-weight: 200 !important;

  .register-input {
    background: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px !important;
    color: white !important;

    &::placeholder {
      color: rgba(255, 255, 255, 0.6) !important;
      font-weight: 300 !important;
    }
  }
}

// Global modal styling overrides
:deep(.modal-content) {
  background: var(--theme-modal-bg) !important;
  border: none !important;
  border-radius: 15px !important;
  overflow: hidden;
}

:deep(.modal-header) {
  background: var(--theme-modal-bg) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 15px 15px 0 0 !important;
}

:deep(.modal-body) {
  background: var(--theme-modal-bg) !important;
  padding: 0 !important;
}

:deep(.modal-dialog) {
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 10px auto;
  }
}

// Override modal backdrop
:deep(.modal-backdrop) {
  background-color: rgba(0, 0, 0, 0.8) !important;
}

// Ensure all modal elements are dark
:deep(.modal) {
  .modal-content * {
    color: white !important;
  }
}

:deep(.btn-outline-light) {
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  font-weight: 600;
  padding: 10px 20px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  }
}

// Target the specific modal ID and all Bootstrap Vue modal variations
#register-modal___BV_modal_outer_,
div[id*='register-modal'] {
  .modal-dialog {
    .modal-content {
      background: var(--theme-modal-bg) !important;
      border: none !important;
    }

    .modal-header {
      background: var(--theme-modal-bg) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;

      h4,
      .modal-title {
        color: white !important;
      }
    }

    .modal-body {
      background: var(--theme-modal-bg) !important;
      padding: 0 !important;
    }
  }
}

.vue-form-wizard {
  background: var(--theme-modal-bg) !important;
  padding: 0px !important;

  border-radius: 0px;
  .alert-body {
    font-size: 13px;
    font-weight: 300;
  }

  .wizard-nav {
    padding: 10px 0px 0px 0px !important;
    border: 0px !important;
    display: none !important; // Hide the step navigation
  }

  .wizard-nav li {
    padding-bottom: 18px !important;
  }

  .wizard-tab-content {
    padding: 0px 20px 10px;
  }

  .wizard-card-footer {
    margin-top: 5px;
  }

  .wizard-footer-left .wizard-btn {
    color: #fff !important;
  }

  label {
    font-size: 14px !important;
    padding: 5px;
    color: #fff !important;
  }
}
</style>
